import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  cashToInvest: "Cash to invest",
  cashToInvestDescription: (amount: string) => `Clients with more than ${amount} in cash`,
  colClient: "Client",
  colCash: (currency: string) => `Cash (${currency})`,
  colShare: "Share of portfolio",
  noCash: "No clients have more cash than the threshold.",
  clientCount: (n: string) => `${n} clients`,
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    cashToInvest: "Liquidités à investir",
    cashToInvestDescription: (amount) => `Clients disposant de plus de ${amount} de liquidités`,
    colClient: "Client",
    colCash: (currency) => `Liquidités (${currency})`,
    colShare: "Part du portefeuille",
    noCash: "Aucun client ne dispose de liquidités supérieures au seuil.",
    clientCount: (n) => `${n} clients`,
  },
  es_ES: {
    cashToInvest: "Efectivo para invertir",
    cashToInvestDescription: (amount) => `Clientes con más de ${amount} en efectivo`,
    colClient: "Cliente",
    colCash: (currency) => `Efectivo (${currency})`,
    colShare: "Peso en la cartera",
    noCash: "Ningún cliente tiene más efectivo que el umbral.",
    clientCount: (n) => `${n} clientes`,
  },
  pt_BR: {
    cashToInvest: "Caixa para investir",
    cashToInvestDescription: (amount) => `Clientes com mais de ${amount} em caixa`,
    colClient: "Cliente",
    colCash: (currency) => `Caixa (${currency})`,
    colShare: "Participação na carteira",
    noCash: "Nenhum cliente tem mais caixa do que o limite.",
    clientCount: (n) => `${n} clientes`,
  },
  de_DE: {
    cashToInvest: "Liquidität zum Investieren",
    cashToInvestDescription: (amount) => `Kunden mit mehr als ${amount} Liquidität`,
    colClient: "Kunde",
    colCash: (currency) => `Liquidität (${currency})`,
    colShare: "Anteil am Portfolio",
    noCash: "Kein Kunde hat mehr Liquidität als den Schwellenwert.",
    clientCount: (n) => `${n} Kunden`,
  },
  it_IT: {
    cashToInvest: "Liquidità da investire",
    cashToInvestDescription: (amount) => `Clienti con più di ${amount} di liquidità`,
    colClient: "Cliente",
    colCash: (currency) => `Liquidità (${currency})`,
    colShare: "Quota del portafoglio",
    noCash: "Nessun cliente ha liquidità superiore alla soglia.",
    clientCount: (n) => `${n} clienti`,
  },
  ar: {
    cashToInvest: "نقد متاح للاستثمار",
    cashToInvestDescription: (amount) => `العملاء الذين لديهم نقد يزيد على ${amount}`,
    colClient: "العميل",
    colCash: (currency) => `النقد (${currency})`,
    colShare: "النسبة من المحفظة",
    noCash: "لا يوجد عملاء لديهم نقد يتجاوز الحد.",
    clientCount: (n) => `${n} عملاء`,
  },
  ja_JP: {
    cashToInvest: "投資可能な現金",
    cashToInvestDescription: (amount) => `現金が ${amount} を超える顧客`,
    colClient: "顧客",
    colCash: (currency) => `現金（${currency}）`,
    colShare: "ポートフォリオに占める割合",
    noCash: "現金がしきい値を超える顧客はいません。",
    clientCount: (n) => `顧客 ${n} 名`,
  },
  hi_IN: {
    cashToInvest: "निवेश योग्य नकद",
    cashToInvestDescription: (amount) => `${amount} से अधिक नकद वाले क्लाइंट`,
    colClient: "क्लाइंट",
    colCash: (currency) => `नकद (${currency})`,
    colShare: "पोर्टफ़ोलियो में हिस्सा",
    noCash: "किसी भी क्लाइंट के पास सीमा से अधिक नकद नहीं है।",
    clientCount: (n) => `${n} क्लाइंट`,
  },
  zh_CN: {
    cashToInvest: "可投资现金",
    cashToInvestDescription: (amount) => `现金超过 ${amount} 的客户`,
    colClient: "客户",
    colCash: (currency) => `现金 (${currency})`,
    colShare: "占投资组合比例",
    noCash: "没有客户的现金超过阈值。",
    clientCount: (n) => `${n} 位客户`,
  },
  zh_TW: {
    cashToInvest: "可投資現金",
    cashToInvestDescription: (amount) => `現金超過 ${amount} 的客戶`,
    colClient: "客戶",
    colCash: (currency) => `現金 (${currency})`,
    colShare: "占投資組合比例",
    noCash: "沒有客戶的現金超過門檻。",
    clientCount: (n) => `${n} 位客戶`,
  },
  nl_NL: {
    cashToInvest: "Te beleggen liquiditeit",
    cashToInvestDescription: (amount) => `Klanten met meer dan ${amount} aan liquide middelen`,
    colClient: "Klant",
    colCash: (currency) => `Liquide middelen (${currency})`,
    colShare: "Aandeel in portefeuille",
    noCash: "Geen enkele klant heeft meer liquide middelen dan de drempel.",
    clientCount: (n) => `${n} klanten`,
  },
  nb_NO: {
    cashToInvest: "Kontanter å investere",
    cashToInvestDescription: (amount) => `Kunder med mer enn ${amount} i kontanter`,
    colClient: "Kunde",
    colCash: (currency) => `Kontanter (${currency})`,
    colShare: "Andel av porteføljen",
    noCash: "Ingen kunder har mer kontanter enn terskelen.",
    clientCount: (n) => `${n} kunder`,
  },
  sv_SE: {
    cashToInvest: "Likvida medel att investera",
    cashToInvestDescription: (amount) => `Kunder med mer än ${amount} i likvida medel`,
    colClient: "Kund",
    colCash: (currency) => `Likvida medel (${currency})`,
    colShare: "Andel av portföljen",
    noCash: "Inga kunder har mer likvida medel än tröskelvärdet.",
    clientCount: (n) => `${n} kunder`,
  },
  fi_FI: {
    cashToInvest: "Sijoitettava käteinen",
    cashToInvestDescription: (amount) => `Asiakkaat, joilla on käteistä yli ${amount}`,
    colClient: "Asiakas",
    colCash: (currency) => `Käteinen (${currency})`,
    colShare: "Osuus salkusta",
    noCash: "Yhdelläkään asiakkaalla ei ole käteistä yli raja-arvon.",
    clientCount: (n) => `${n} asiakasta`,
  },
  da_DK: {
    cashToInvest: "Kontanter til investering",
    cashToInvestDescription: (amount) => `Kunder med mere end ${amount} i kontanter`,
    colClient: "Kunde",
    colCash: (currency) => `Kontanter (${currency})`,
    colShare: "Andel af porteføljen",
    noCash: "Ingen kunder har flere kontanter end grænsen.",
    clientCount: (n) => `${n} kunder`,
  },
  is_IS: {
    cashToInvest: "Reiðufé til fjárfestingar",
    cashToInvestDescription: (amount) => `Viðskiptavinir með meira en ${amount} í reiðufé`,
    colClient: "Viðskiptavinur",
    colCash: (currency) => `Reiðufé (${currency})`,
    colShare: "Hlutfall af eignasafni",
    noCash: "Engir viðskiptavinir eiga meira reiðufé en viðmiðunarmörkin.",
    clientCount: (n) => `${n} viðskiptavinir`,
  },
};
